import { useCallback, useRef } from 'react';
import { generateStringId } from '@/lib/ids';

/**
 * A per-instance cache of generated ids, keyed by the component's position in its config
 * array. Replaces the legacy `componentsReference` object in `moduleLoader.vue` /
 * `groupWidget.vue`.
 *
 * ## Why this matters (CLAUDE.md constraint 3)
 *
 * The id returned here is the store key for a widget's data *and* the value the topic map
 * points at. If it changes on a re-render, the widget's `messages[componentId]` slot moves,
 * its old topic registration stays behind pointing at a component that no longer exists, and
 * the topic list the preventer ACL was provisioned from stops matching what the app
 * subscribes to. Nothing throws; the screen simply stops updating, and only on a real
 * display. So the id must be generated once and then never again.
 *
 * ## Why a ref and not module scope
 *
 * The legacy version kept the map at module scope. That leaks ids between component
 * instances (two `GroupWidget`s would share one map and therefore one set of ids), survives
 * hot reloads with stale entries, and survives `reset()` in tests. A ref is scoped to the
 * instance and dies with it.
 *
 * StrictMode's double render and its mount/unmount/mount effect cycle both reuse the same
 * fiber, so the ref — and every id in it — survives them. Ids are only regenerated by a real
 * remount, which in this app means a page reload.
 *
 * ## Why the key is the index and not the content
 *
 * The index is what legacy used and it is stable because the config is immutable at runtime:
 * nothing reorders, inserts or removes a component. A content hash would look safer and be
 * worse — two identical widget configs (two `TextWidget`s with the same options, which is a
 * legal config) would hash equal, collide onto one id, and share one store slot.
 *
 * ```ts
 * const getId = useStableIds();
 * const componentId = getId(index); // same id for the same index, forever
 * ```
 */
export type GetStableId = (key: number | string) => string;

export function useStableIds(): GetStableId {
  const idsRef = useRef<Map<number | string, string>>(new Map());

  // `getId` is called during render, which makes it technically impure — it writes to a ref.
  // This is the sanctioned exception: a write-once memo whose value is never read by anything
  // that could observe the difference, and the alternative (generating ids in an effect) would
  // render one frame with no ids at all and register every topic a frame late.
  return useCallback((key) => {
    const existing = idsRef.current.get(key);
    if (existing !== undefined) return existing;

    const id = generateStringId();
    idsRef.current.set(key, id);
    return id;
  }, []);
}
